// Root component — owns the boot → picker → pin → shell stage machine.
// All timer/queue state lives inside <TimeTracker>; this file only handles
// session lifecycle and routing between the three login stages.

import { useEffect, useState } from 'react';
import { api } from './lib/api.js';
import { initTheme } from './lib/theme.js';
import { loadSession, saveSession, clearSession, setActiveBase } from './lib/session.js';
import EmailLogin from './components/EmailLogin.jsx';
import PinPad from './components/PinPad.jsx';
import AppShell from './components/AppShell.jsx';

export default function App() {
  const [stage, setStage] = useState('boot'); // 'boot' | 'email' | 'check-email' | 'pin' | 'shell'
  const [session, setSession] = useState(null);
  const [pendingEmployee, setPendingEmployee] = useState(null);
  const [pinMode, setPinMode] = useState(null); // 'verify' | 'setup'
  const [pickerError, setPickerError] = useState(null);
  const [resetBanner, setResetBanner] = useState(null);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendNotice, setResendNotice] = useState(null);

  useEffect(() => {
    initTheme();

    // Magic-link reset: ?token=… in the URL means the user clicked a
    // PIN-reset link from email. Consume the token (which deletes their
    // PIN server-side), then drop them into the "set new PIN" flow.
    // Any existing session is wiped because the reset is conceptually a
    // re-onboarding for this user.
    const url = new URL(window.location.href);
    const resetToken = url.searchParams.get('token');
    if (resetToken) {
      // Strip the token from the URL immediately so a refresh doesn't
      // retry the verification (which would fail — single-use token).
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url.toString());
      clearSession();

      api.verifyPinReset(resetToken)
        .then(({ employee }) => {
          setPendingEmployee(employee);
          setPinMode('setup');
          setResetBanner('PIN reset confirmed — choose a new PIN below.');
          setStage('pin');
        })
        .catch(err => {
          setPickerError(err.message || 'Reset link is invalid or has expired.');
          setStage('email');
        });
      return;
    }

    const existing = loadSession(); // also calls setApiBaseId
    if (existing?.employee) {
      setSession(existing);
      setStage('shell');
    } else {
      setStage('email');
    }
  }, []);

  const decideStageForEmployee = async (emp) => {
    setPendingEmployee(emp);
    try {
      const status = await api.pinStatus(emp.id);
      if (status.hasPin) {
        // Returning user — straight to PIN verify.
        setPinMode('verify');
        setStage('pin');
      } else {
        // First-time user — don't let them set a PIN directly from the
        // browser they typed an email into; that would let anyone who
        // knows a colleague's email claim that colleague's account.
        // Instead, fire the magic-link flow (same Zapier-driven email
        // used for PIN reset) and route them through email confirmation.
        // When they click the link, the existing /reset?token=… handler
        // verifies them and drops them straight into PinPad-setup.
        try { await api.requestPinReset(emp.email); } catch {}
        setResendNotice(null);
        setStage('check-email');
      }
    } catch (e) {
      setPickerError(e.message);
      setPendingEmployee(null);
      setStage('email');
    }
  };

  const handleResendSetupLink = async () => {
    if (!pendingEmployee?.email || resendBusy) return;
    setResendBusy(true);
    setResendNotice(null);
    try {
      const res = await api.requestPinReset(pendingEmployee.email);
      // Server returns {ok:true, throttled:true} when the 60s rate
      // limit is in effect. Treat as soft success — the previous
      // email is still on its way; just tell the user to wait.
      if (res?.throttled) {
        setResendNotice('A link was just sent. Give it a minute before requesting another.');
      } else {
        setResendNotice('Setup link sent. Check your inbox.');
      }
    } catch (e) {
      setResendNotice(e.message || 'Could not resend the link.');
    } finally {
      setResendBusy(false);
    }
  };

  const handleSetupBackToEmail = () => {
    setPendingEmployee(null);
    setResendNotice(null);
    setStage('email');
  };

  const handleEmailResolved = (emp) => decideStageForEmployee(emp);

  const handlePinSuccess = () => {
    const emp = pendingEmployee;
    if (!emp) return;
    const newSession = { employee: emp, activeBaseId: emp.baseId };
    setSession(newSession);
    saveSession(newSession); // also calls setApiBase
    setPendingEmployee(null);
    setPinMode(null);
    setResetBanner(null);
    setStage('shell');
  };

  const handleSwitchBase = (baseId) => {
    if (!session) return;
    const next = setActiveBase(session, baseId);
    setSession({ ...next });
  };

  const handlePinBack = () => {
    setPendingEmployee(null);
    setPinMode(null);
    setResetBanner(null);
    setStage('email');
  };

  const handleLogout = async () => {
    // Best-effort: pause any running timer for this user before
    // clearing the session. Without this, a user who logs out
    // mid-timer would leave an open Time Log in Airtable until the
    // 4h sweep reaped it. The pause uses the still-bound activeBase
    // (cleared by clearSession in the next step), so its X-HLPT-Base
    // header reaches the correct base.
    //
    // HR-only execs (bases:[]) have no team base to pause against —
    // calling stopCurrentTimer for them would hit /api/queue-action
    // without an X-HLPT-Base header and 400. Skip the call entirely;
    // there's no Time Logs surface to clean up for these users.
    const uid = session?.employee?.id;
    const hasTeamBase = Array.isArray(session?.employee?.bases) && session.employee.bases.length > 0;
    if (uid && hasTeamBase) {
      try { await api.stopCurrentTimer(uid); } catch {}
    }
    clearSession(); // also calls setApiBase(null)
    setSession(null);
    setPendingEmployee(null);
    setPinMode(null);
    setStage('email');
  };

  if (stage === 'boot') {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted text-sm">
        Loading…
      </div>
    );
  }

  if (stage === 'email') {
    return (
      <>
        {pickerError && (
          <div className="max-w-md mx-auto mt-4 p-3 rounded-lg border border-danger/30 bg-danger/10 text-sm text-danger">
            {pickerError}
          </div>
        )}
        <EmailLogin onResolved={handleEmailResolved} />
      </>
    );
  }

  if (stage === 'pin' && pendingEmployee) {
    return (
      <>
        {resetBanner && (
          <div className="max-w-sm mx-auto mt-4 p-3 rounded-lg border border-running/30 bg-running/10 text-sm text-running">
            {resetBanner}
          </div>
        )}
        <PinPad
          employee={pendingEmployee}
          mode={pinMode}
          onSuccess={handlePinSuccess}
          onBack={handlePinBack}
        />
      </>
    );
  }

  if (stage === 'check-email' && pendingEmployee) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="rounded-2xl border border-border bg-panel p-6 shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-accent/15 text-accent flex items-center justify-center mx-auto mb-4">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <h1 className="text-center text-lg font-semibold">Check your email</h1>
            <p className="text-center text-sm text-muted mt-2 leading-relaxed">
              We sent a setup link to{' '}
              <span className="text-text font-medium">{pendingEmployee.email}</span>.
              Click it to set your PIN. The link expires in 10 minutes.
            </p>
            <p className="text-center text-[11px] text-muted mt-3">
              First time signing in? We do this to confirm the email is yours
              before you pick a PIN.
            </p>

            {resendNotice && (
              <div className="mt-4 text-center text-xs text-text/80">{resendNotice}</div>
            )}

            <div className="mt-5 flex flex-col gap-2">
              <button
                onClick={handleResendSetupLink}
                disabled={resendBusy}
                className="w-full px-3 py-2 rounded-md bg-accent hover:bg-accent2 text-white text-sm font-semibold transition-colors disabled:opacity-60"
              >
                {resendBusy ? 'Sending…' : 'Resend link'}
              </button>
              <button
                onClick={handleSetupBackToEmail}
                className="w-full px-3 py-2 rounded-md text-sm text-muted hover:text-text transition-colors"
              >
                Use a different email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'shell' && session?.employee) {
    return (
      <AppShell
        employee={session.employee}
        activeBaseId={session.activeBaseId || session.employee.baseId}
        onSwitchBase={handleSwitchBase}
        onLogout={handleLogout}
      />
    );
  }

  return null;
}
